import { Subscribable, Subscription } from '@benzinga/subscribable';
import { SafePromise } from '@benzinga/safe-await';
import { Session } from '@benzinga/session';
import { TrendingTopic } from './entities';
import { TrendingTopicRequestEvent, TrendingTopicsRequest } from './request';
import { TrendingTopicsStore } from './store';

export type TrendingTopicsManagerEvent = TrendingTopicRequestEvent;

export class TrendingTopicsManager extends Subscribable<TrendingTopicsManagerEvent> {
  private request: TrendingTopicsRequest;
  private store: TrendingTopicsStore;
  private requestSubscription?: Subscription<TrendingTopicsRequest>;

  constructor(session: Session) {
    super();
    this.request = new TrendingTopicsRequest(session);
    this.store = new TrendingTopicsStore();
    this.fetchTrendingTopics(true);
  }

  public static getName = () => 'benzinga-trending-topics';

  public getTrendingTopics = async (
    taxonomies?: TrendingTopic['taxonomy'][],
    force?: boolean,
  ): SafePromise<TrendingTopic[]> => {
    const topics = await this.fetchTrendingTopics(force);
    if (topics.ok) {
      return {
        ok: topics.ok.filter(topic => !taxonomies || taxonomies.some(taxonomy => topic.taxonomy === taxonomy)),
      };
    }
    return topics;
  };

  public getTrendingIndiaTopics = async (force?: boolean): SafePromise<TrendingTopic[]> => {
    return await this.fetchTrendingIndiaTopics(force);
  };

  protected fetchTrendingTopics = async (force?: boolean): SafePromise<TrendingTopic[]> => {
    if (!force && !this.store.shouldFetchTrendingTopics()) {
      return { ok: this.store.getTrendingTopics() };
    }

    const topics = await this.request.getTopics();
    if (topics.ok) {
      this.store.setTrendingTopics(topics.ok);
      return { ok: topics.ok };
    }
    return topics;
  };

  protected fetchTrendingIndiaTopics = async (force?: boolean): SafePromise<TrendingTopic[]> => {
    if (!force && !this.store.shouldFetchTrendingIndiaTopics()) {
      return { ok: this.store.getTrendingIndiaTopics() };
    }

    const topics = await this.request.getTrendingIndiaTopics();
    if (topics.ok) {
      this.store.setTrendingIndiaTopics(topics.ok);
      return { ok: topics.ok };
    }
    return topics;
  };

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
  }
}
